import type { IndexDef } from '@/api/types/readiness'
import type { AnnexAsset, MigrationPlanVersion, SystemAnnex } from '@/api/types/migration'
import { ASSET_FIELDS, NOTES_FIELD, section3Text } from '@/api/types/migration'
import { ANNEX_SECTIONS, CHALLENGE_COLUMNS, PART_A_SECTIONS, fmtDay, protocolsGroupedText } from '@/lib/migration/engine'

// ── Submission plan export (I3 §11, Note 5) ─────────────────────────────────
// The .docx follows the CSA template's headings and field order: Part A once,
// then one Part B per annex in rail order with one section-2 block per asset.
// It is rendered from a *version's snapshot*, never from the live plan, so
// re-rendering a version reproduces its export (§5.2). PDF goes through the
// print route (/readiness/submission/versions/{id}/print) and the I2 §10
// print stylesheet. Both carry the Quantizant footer.

export const slug = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()

export function planFilenames(index: IndexDef, version: MigrationPlanVersion) {
  const base = `${slug(index.name)}-migration-plan-${slug(version.label)}`
  return { docx: `${base}.docx`, pdf: `${base}.pdf`, zip: `${base}-with-export-log.zip`, log: `${base}-export-log.txt` }
}

export function footerText(index: IndexDef, version: MigrationPlanVersion, at = new Date()) {
  return `Generated by Quantizant Readiness · ${index.name} ${index.version} · ${version.label} · ${fmtDay(at.toISOString())}`
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 2000)
}

// The rendered form of every Part A section, shared by docx and print.
export function partARows(version: MigrationPlanVersion): Array<{ n: number; title: string; csa: string; lines: string[]; options?: Array<{ label: string; ticked: boolean }>; table?: Array<[string, string]> }> {
  const p = version.snapshot.plan
  return PART_A_SECTIONS.map((s) => {
    if (s.kind === 'text') return { n: s.n, title: s.title, csa: s.csa, lines: [String(p[s.key] || '')] }
    if (s.kind === 'table') return { n: s.n, title: s.title, csa: s.csa, lines: [], table: p.s6_challenges.map((c) => [c.blocker, c.support_needed] as [string, string]) }
    const sec = p[s.key] as { options: string[]; other_text: string; elaboration: string }
    return {
      n: s.n,
      title: s.title,
      csa: s.csa,
      lines: [sec.elaboration],
      options: (s.options ?? []).map((o) => ({ label: o.value === 'other' && sec.other_text ? `Other: ${sec.other_text}` : o.label, ticked: sec.options.includes(o.value) })),
    }
  })
}

export const blocksOfAnnex = (version: MigrationPlanVersion, annex: SystemAnnex): AnnexAsset[] => version.snapshot.assets.filter((b) => b.annex_id === annex.id).sort((a, b) => a.sort_order - b.sort_order)

// The template's Y/N row: Y with the solution and timeline, N with the
// justification and mitigation controls. Both are typed into the same field.
export function willMigrateAnswer(b: AnnexAsset): string {
  if (!b.will_migrate) return ''
  const detail = b.migrate_solution_timeline.trim()
  if (!detail) return b.will_migrate
  return /^[YN]\s*[·—–-]/.test(detail) ? detail : `${b.will_migrate} · ${detail}`
}

// Section 1 rows, in the template's order.
export const systemRows = (a: SystemAnnex): Array<[string, string]> => [
  ['System name', a.system_name],
  ['Brief description of system', a.description],
  ['Function supported', a.function_supported],
]

// Every row of the template's section-2 table, labelled as the template
// labels them, notes last.
export const assetRows = (b: AnnexAsset): Array<[string, string]> => [
  ...ASSET_FIELDS.map((f) => [f.csa, f.key === 'will_migrate' ? willMigrateAnswer(b) : f.key === 'protocols_algorithms' ? protocolsGroupedText(b) : String(b[f.key] ?? '')] as [string, string]),
  [NOTES_FIELD.csa, b.notes],
]

export async function buildPlanDocx(index: IndexDef, version: MigrationPlanVersion, exportedAt = new Date()): Promise<Blob> {
  const { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, WidthType, Footer, AlignmentType } = await import('docx')
  const p = version.snapshot.plan
  const kids: InstanceType<typeof Paragraph | typeof Table>[] = []
  const h = (text: string, level: (typeof HeadingLevel)[keyof typeof HeadingLevel]) => new Paragraph({ text, heading: level })
  const para = (text: string, opts: { italics?: boolean; bold?: boolean } = {}) => new Paragraph({ children: [new TextRun({ text, ...opts })] })
  // One paragraph per line, so grouped protocols and multi-line notes keep their breaks.
  const cell = (text: string, bold = false, width?: number) => new TableCell({ children: text.split('\n').map((line) => new Paragraph({ children: [new TextRun({ text: line, bold })] })), width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined })
  const kv = (rows: Array<[string, string]>) => new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: rows.map(([k, v]) => new TableRow({ children: [cell(k, true, 34), cell(v || '—', false, 66)] })) })

  kids.push(new Paragraph({ children: [new TextRun({ text: 'Cyber Security Agency of Singapore · Quantum-Safe Migration Plan Template', italics: true })], alignment: AlignmentType.LEFT }))
  kids.push(h('Quantum-Safe Migration Plan', HeadingLevel.TITLE))
  kids.push(h('Part A — Organisational Level', HeadingLevel.HEADING_1))
  kids.push(kv([
    ['Organisation name', p.org_name],
    ['CII sector', p.cii_sector],
    ['Accountable executive', [p.accountable_exec_name, p.accountable_exec_title].filter(Boolean).join(', ')],
    ['Point(s) of contact', p.contacts.map((c) => [c.name, c.title, c.email].filter(Boolean).join(' · ')).join('; ')],
    ['Submission date', fmtDay(version.submission_date)],
    ['Version', version.label],
    ...(version.note?.trim() ? [['Key changes in this version', version.note.trim()] as [string, string]] : []),
  ]))
  partARows(version).forEach((s) => {
    kids.push(h(`Section ${s.n} · ${s.title}`, HeadingLevel.HEADING_2))
    kids.push(para(s.csa, { italics: true }))
    s.options?.forEach((o) => kids.push(para(`${o.ticked ? '☒' : '☐'} ${o.label}`)))
    s.lines.filter(Boolean).forEach((l) => kids.push(para(l)))
    if (s.table) {
      if (!s.table.length) kids.push(para('—'))
      else kids.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [new TableRow({ children: [cell(CHALLENGE_COLUMNS.blocker, true, 50), cell(CHALLENGE_COLUMNS.support, true, 50)] }),...s.table.map(([b, sup]) => new TableRow({ children: [cell(b), cell(sup)] }))] }))
    }
  })

  const annexes = [...version.snapshot.annexes].sort((a, b) => a.sort_order - b.sort_order)
  annexes.forEach((a, i) => {
    kids.push(new Paragraph({ text: '', pageBreakBefore: true }))
    kids.push(h(`Part B — System Annex ${i + 1} of ${annexes.length} · ${a.system_name || 'Untitled system'}`, HeadingLevel.HEADING_1))
    kids.push(h(`Section 1 · ${ANNEX_SECTIONS.s1.title}`, HeadingLevel.HEADING_2))
    kids.push(kv(systemRows(a)))
    kids.push(h(`Section 2 · ${ANNEX_SECTIONS.s2.title}`, HeadingLevel.HEADING_2))
    const blocks = blocksOfAnnex(version, a)
    if (!blocks.length) kids.push(para('No cryptographic assets recorded.', { italics: true }))
    blocks.forEach((b, k) => {
      kids.push(h(`Asset ${k + 1} of ${blocks.length} · ${b.asset_model_version || 'Unnamed asset'}`, HeadingLevel.HEADING_3))
      kids.push(kv(assetRows(b)))
    })
    kids.push(h(`Section 3 · ${ANNEX_SECTIONS.s3.title}`, HeadingLevel.HEADING_2))
    const s3 = section3Text(a)
    if (!s3) kids.push(para('—'))
    else s3.split(/\n+/).filter((l) => l.trim()).forEach((l) => kids.push(para(l)))
  })
  if (!annexes.length) kids.push(para('No Part B annexes.', { italics: true }))

  const doc = new Document({
    sections: [{ footers: { default: new Footer({ children: [new Paragraph({ children: [new TextRun({ text: footerText(index, version, exportedAt), size: 16, color: '7b8794' })] })] }) }, children: kids }],
  })
  return Packer.toBlob(doc)
}

export function buildExportLog(index: IndexDef, version: MigrationPlanVersion, exportedAt = new Date()): Blob {
  const lines = [
    footerText(index, version, exportedAt),
    `Version: ${version.label} · submission date ${fmtDay(version.submission_date)} · saved by ${version.created_by} · ${version.created_at}`,
    `Completeness: ${version.completeness}% · blocking checks: ${version.blocking_count} · formats: ${version.exported_formats.join(', ') || 'none'}`,
    '',
    'Needs attention (non-blocking):',
    ...(version.attention_items.length ? version.attention_items.map((c) => `- ${c.text}`) : ['- none']),
    '',
    'Log:',
    ...version.export_log.map((l) => `- ${l}`),
  ]
  return new Blob([lines.join('\n')], { type: 'text/plain' })
}

// "Both · zipped with the export log": a stored (uncompressed) zip built in
// the browser, no library needed.
export async function buildZip(files: Array<{ name: string; blob: Blob }>): Promise<Blob> {
  const enc = new TextEncoder()
  const parts: Uint8Array[] = []
  const central: Uint8Array[] = []
  let offset = 0
  const crcTable = (() => {
    const t = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      t[n] = c >>> 0
    }
    return t
  })()
  const crc32 = (buf: Uint8Array) => {
    let c = 0xffffffff
    for (let i = 0; i < buf.length; i++) c = crcTable[(c ^ buf[i]) & 0xff] ^ (c >>> 8)
    return (c ^ 0xffffffff) >>> 0
  }
  const u16 = (n: number) => new Uint8Array([n & 0xff, (n >> 8) & 0xff])
  const u32 = (n: number) => new Uint8Array([n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >>> 24) & 0xff])
  const cat = (...xs: Uint8Array[]) => {
    const out = new Uint8Array(xs.reduce((a, x) => a + x.length, 0))
    let o = 0
    xs.forEach((x) => { out.set(x, o); o += x.length })
    return out
  }
  for (const f of files) {
    const data = new Uint8Array(await f.blob.arrayBuffer())
    const name = enc.encode(f.name)
    const crc = crc32(data)
    const local = cat(u32(0x04034b50), u16(20), u16(0), u16(0), u16(0), u16(0), u32(crc), u32(data.length), u32(data.length), u16(name.length), u16(0), name, data)
    central.push(cat(u32(0x02014b50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0), u32(crc), u32(data.length), u32(data.length), u16(name.length), u16(0), u16(0), u16(0), u16(0), u32(0), u32(offset), name))
    parts.push(local)
    offset += local.length
  }
  const cd = cat(...central)
  const end = cat(u32(0x06054b50), u16(0), u16(0), u16(files.length), u16(files.length), u32(cd.length), u32(offset), u16(0))
  return new Blob([cat(...parts, cd, end)], { type: 'application/zip' })
}
